/**
 * Dashboard Home View
 * 
 * Overview of key metrics, active alerts, and system status.
 */

import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, TrendingUp, Globe, Shield, 
  Activity, Zap, Target, Clock 
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';

// Components
import MetricCard from '../components/MetricCard';
import AlertPanel from '../components/AlertPanel';

import ThreatGauge from '../components/ThreatGauge';

export default function DashboardHome({ apiUrl }) {
  const [status, setStatus] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [recentPredictions, setRecentPredictions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  async function loadDashboardData() {
    try {
      const [statusRes, alertsRes] = await Promise.all([
        fetch(`${apiUrl}/status`).then(r => r.json()),
        fetch(`${apiUrl}/alerts`).then(r => r.json())
      ]);
      
      setStatus(statusRes);
      setAlerts(alertsRes.alerts || []);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      setLoading(false);
    }
  }

  // Mock data for charts
  const threatTrendData = [
    { time: '00:00', global: 0.32, regional: 0.45, economic: 0.28 },
    { time: '04:00', global: 0.35, regional: 0.42, economic: 0.31 },
    { time: '08:00', global: 0.38, regional: 0.48, economic: 0.29 },
    { time: '12:00', global: 0.42, regional: 0.52, economic: 0.35 },
    { time: '16:00', global: 0.39, regional: 0.49, economic: 0.33 },
    { time: '20:00', global: 0.41, regional: 0.51, economic: 0.32 },
    { time: 'Now', global: 0.43, regional: 0.54, economic: 0.34 },
  ];

  const hotspots = [
    { name: 'Taiwan Strait', level: 0.72, trend: 'rising' },
    { name: 'Ukraine', level: 0.68, trend: 'stable' },
    { name: 'South China Sea', level: 0.55, trend: 'rising' },
    { name: 'Korean Peninsula', level: 0.48, trend: 'stable' },
    { name: 'Middle East', level: 0.62, trend: 'rising' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Global Threat Overview</h1>
          <p className="text-slate-400">Real-time crisis monitoring and prediction</p>
        </div>
        <div className="flex items-center space-x-2 text-sm text-slate-400">
          <Clock className="w-4 h-4" />
          <span>Last updated: {new Date().toLocaleTimeString()}</span>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Global Threat Level"
          value="ELEVATED"
          change="+5%"
          trend="up"
          icon={AlertTriangle}
          color="yellow"
        />
        <MetricCard
          title="Active Alerts"
          value={alerts.length}
          change={alerts.filter(a => !a.acknowledged).length + " unread"}
          trend="neutral"
          icon={Zap}
          color="red"
        />
        <MetricCard
          title="Monitored Regions"
          value={status?.monitoringTargets?.length || 0}
          change="Active"
          trend="neutral"
          icon={Globe}
          color="cyan"
        />
        <MetricCard
          title="System Accuracy"
          value="95%"
          change="Target met"
          trend="up"
          icon={Target}
          color="green"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Threat Trend Chart */}
        <div className="lg:col-span-2 bg-slate-800 rounded-xl p-6 border border-slate-700">
          <h2 className="text-lg font-semibold mb-4">Threat Index Trend (24h)</h2>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={threatTrendData}>
              <defs>
                <linearGradient id="globalGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="regionalGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <XAxis dataKey="time" stroke="#64748b" />
              <YAxis stroke="#64748b" domain={[0, 1]} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1e293b', 
                  border: '1px solid #334155',
                  borderRadius: '8px'
                }} 
              />
              <Area 
                type="monotone" 
                dataKey="global" 
                stroke="#06b6d4" 
                fill="url(#globalGradient)"
                name="Global"
              />
              <Area 
                type="monotone" 
                dataKey="regional" 
                stroke="#f59e0b" 
                fill="url(#regionalGradient)"
                name="Regional"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Global Threat Gauge */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <h2 className="text-lg font-semibold mb-4">Current Threat Level</h2>
          <ThreatGauge value={0.43} />
          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Confidence</span>
              <span className="text-cyan-400">87%</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Prediction Window</span>
              <span className="text-cyan-400">30 days</span>
            </div>
          </div>
        </div>
      </div>

      {/* Hotspots and Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Crisis Hotspots */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <h2 className="text-lg font-semibold mb-4">Crisis Hotspots</h2>
          <div className="space-y-3">
            {hotspots.map((hotspot, i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className={`w-2 h-2 rounded-full mr-3 ${
                    hotspot.level > 0.7 ? 'bg-red-500' :
                    hotspot.level > 0.5 ? 'bg-yellow-500' : 'bg-green-500'
                  }`} />
                  <span>{hotspot.name}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-24 bg-slate-700 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${
                        hotspot.level > 0.7 ? 'bg-red-500' :
                        hotspot.level > 0.5 ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${hotspot.level * 100}%` }}
                    />
                  </div>
                  <span className="text-sm text-slate-400 w-12">
                    {(hotspot.level * 100).toFixed(0)}%
                  </span>
                  {hotspot.trend === 'rising' && (
                    <TrendingUp className="w-4 h-4 text-red-400" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Alerts */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <h2 className="text-lg font-semibold mb-4">Recent Alerts</h2>
          <AlertPanel alerts={alerts.slice(0, 5)} />
        </div>
      </div>

      {/* System Modules Status */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <h2 className="text-lg font-semibold mb-4">System Modules</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {status?.modules?.map((module, i) => (
            <div key={i} className="bg-slate-700/50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium capitalize">
                  {module.name.replace('_', ' ')}
                </span>
                <div className={`w-2 h-2 rounded-full ${
                  module.status === 'ready' ? 'bg-green-500' : 'bg-yellow-500'
                }`} />
              </div>
              <div className="text-xs text-slate-400">
                {module.status === 'ready' ? 'Operational' : 'Loading...'}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
